import genericMemo from "Common/componentUtils/genericMemo";
import sort from "Common/componentUtils/sort";
import useIsVisible from "Common/componentUtils/useIsVisible";
import withOverlaysProvider from "Components/overlaysContext/withOverlaysProvider";
import ProgressOverlay from "Components/progressOverlay";
import classNames from "classnames";
import compact from "lodash/compact";
import map from "lodash/map";
import some from "lodash/some";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List } from "UiKit";
import styles from "./sidebarMenuList.inner.module.css";
import { FloatSidebarMenuItem } from "../../floatSidebarMenuItem";
function SidebarMenuListInner(props) {
    const virtualScrollRef = useRef(null);
    const containerRef = useRef(null);
    const isVisible = useIsVisible(containerRef);
    const [groupsOpened, setGroupsOpened] = useState({
        mode: "defaultClosed",
        openedByUser: new Set(),
        closedByUser: new Set()
    });
    useEffect(() => {
        var _a;
        isVisible && ((_a = virtualScrollRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo(0));
    }, [isVisible]);
    const handleOpenedChanged = useCallback((key, open) => {
        setGroupsOpened(prev => {
            const openedByUser = new Set(prev.openedByUser);
            if (open)
                openedByUser.add(key);
            else
                openedByUser.delete(key);
            const closedByUser = new Set(prev.closedByUser);
            if (!open)
                closedByUser.add(key);
            else
                closedByUser.delete(key);
            return {
                ...prev,
                openedByUser,
                closedByUser
            };
        });
    }, []);
    const sidebarMenuItems = useMemo(() => sort(compact(map(props.sidebarMenuItems, s => s ? s.filterAndSort(props.filterText, props.sortBy, props.sortOrder) : undefined)), props.sortBy, props.sortOrder), [props.sidebarMenuItems, props.filterText, props.sortBy, props.sortOrder]);
    useEffect(() => {
        setGroupsOpened(prev => {
            const newMode = props.filterText ? "defaultOpened" : "defaultClosed";
            return newMode === prev.mode
                ? prev
                : { mode: newMode, openedByUser: new Set(), closedByUser: new Set() };
        });
    }, [props.filterText]);
    const isSelected = useCallback((key) => Boolean(props.selectedItems && props.selectedItems.has(key)), [props.selectedItems]);
    const isActive = useCallback((key, childKeys, selected) => Boolean(props.activeItems && props.activeItems.has(key)) ||
        Boolean(childKeys.length &&
            props.selectedItems &&
            (selected || some(childKeys, childKey => { var _a; return (_a = props.selectedItems) === null || _a === void 0 ? void 0 : _a.has(childKey); }))), [props.activeItems, props.selectedItems]);
    const isOpened = useCallback((key, childKeys, active) => groupsOpened.openedByUser.has(key) ||
        ((groupsOpened.mode === "defaultOpened" || (childKeys.length > 0 && active)) &&
            !groupsOpened.closedByUser.has(key)), [groupsOpened.mode, groupsOpened.closedByUser, groupsOpened.openedByUser]);
    const handleItemPrepareToRender = useCallback((e) => {
        const item = props.onItemPrepareToRender ? props.onItemPrepareToRender(e) : e;
        if (item.isBtn)
            return item;
        item.selected = isSelected(item.key);
        item.active = isActive(item.key, item.childKeys, item.selected);
        item.opened = isOpened(item.key, item.childKeys, item.active);
        if (item.subItems && item.opened) {
            item.subItems = map(item.subItems, handleItemPrepareToRender);
        }
        return item;
    }, [props.onItemPrepareToRender, isSelected, isActive, isOpened]);
    const itemRender = useCallback((item) => (React.createElement(FloatSidebarMenuItem, { key: item.key, depth: 0, data: item, selected: item.selected, active: item.active, opened: item.opened, subItems: item.subItems, onOpenedChanged: !item.isBtn ? handleOpenedChanged : undefined, onSelectChanged: !item.isBtn ? props.onSelectChanged : undefined, onItemClick: props.onItemClick, onSubmenuShown: props.onSubmenuShown, onItemDoubleClick: props.onItemDoubleClick, filterText: !item.isBtn ? props.filterText : undefined, actionIconColor: props.actionIconColor, className: styles.item, noStyled: true })), [
        handleOpenedChanged,
        props.onSelectChanged,
        props.onItemClick,
        props.actionIconColor,
        props.hasOutsideIndents,
        props.filterText,
        props.margin
    ]);
    return (React.createElement(ProgressOverlay, { show: props.inProgress, className: classNames("overflow-hidden") },
        React.createElement(List, { items: sidebarMenuItems, itemRender: item => itemRender(handleItemPrepareToRender(item)), classNameContainerList: classNames(styles.list, props.className), header: props.header, focusStateEnabled: false })));
}
function SidebarMenuListInnerWithOverlaysProvider(props) {
    const Hoc = useMemo(() => {
        const SidebarMenuListT = (SidebarMenuListInner);
        return withOverlaysProvider(SidebarMenuListT);
    }, []);
    return React.createElement(Hoc, { ...props });
}
export default genericMemo(SidebarMenuListInnerWithOverlaysProvider);
